// Provenance: repo:tds-reconciliation-portal:frontend/src/components/AutoMatchPanel.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Run ids became UUID strings.
//
// Auto-match: preview, review, confirm — in that order, and the order matters.
//
// Whether the resulting matches wait for a reviewer follows the client's
// `manual_match_policy`. On `immediate` a review offered AFTERWARDS would
// never be a gate, so the review is this panel, before anything moves.
//
// What it must not do is overclaim. Measured on the predecessor's run 41:
// 2,367 matches disposed of 4,732 entries worth Rs 6.15 crore and moved the
// run's open exposure by Rs 0.00 — because exposure is per-company
// |sum(26AS) − sum(books)| and matching entries INSIDE a company changes
// neither sum. So every figure here is at ENTRY grain, and the panel says
// plainly that the headline will not move. This is a labour saving, not a way
// of closing the gap, and a screen implying otherwise would be lying.
import { AlertTriangle, Bot, Info } from 'lucide-react'
import { useState } from 'react'

import {
  DEFAULT_RULES, automatchSummary, outcomeLine, useAutomatch,
  useAutomatchPreview, whyNothing,
} from '../api/automatch'
import type { AutomatchResult } from '../api/automatch'
import { Chip } from './badges'
import { Btn, Modal } from './Modal'
import { Money } from './Money'

const fmt = new Intl.NumberFormat('en-IN')

export function AutoMatchPanel({ runId }: { runId: string | undefined }) {
  const [open, setOpen] = useState(false)
  const [rules, setRules] = useState<string[]>(DEFAULT_RULES)
  const [done, setDone] = useState<AutomatchResult | null>(null)
  const [tookEntries, setTookEntries] = useState(0)
  const [tookParties, setTookParties] = useState(0)
  const previewQ = useAutomatchPreview(runId, open)
  const run = useAutomatch(runId)

  const plan = previewQ.data
  const summary = plan ? automatchSummary(plan, rules) : null
  const why = plan ? whyNothing(plan) : null

  const toggle = (rule: string) =>
    setRules((rs) => (rs.includes(rule) ? rs.filter((r) => r !== rule) : [...rs, rule]))

  function confirm() {
    if (!plan || !summary) return
    setTookEntries(summary.entries)
    setTookParties(summary.parties)
    run.mutate(rules, { onSuccess: (r) => setDone(r) })
  }

  function close() {
    setOpen(false)
    setDone(null)
    run.reset()
  }

  return (
    <>
      <Btn size="xs" onClick={() => setOpen(true)} disabled={runId == null}>
        <Bot size={15} /> Auto-match
      </Btn>

      <Modal opened={open} onClose={close} wide title={
        <span className="inline-flex items-center gap-2"><Bot size={18} /> Auto-match</span>
      }>
        <div className="space-y-4">
          {/* What this does and does not do, before any number is shown. */}
          <p className="text-sm text-muted-foreground">
            The engine proposes matches on stated rules; this takes them in bulk
            instead of one click each. Every match it makes is an ordinary match
            — labelled as the machine&apos;s, recorded against you as the person who
            applied the rule, and reversible in one click. Machine matches are
            not carried forward: each run works out its own.
          </p>

          {previewQ.isPending && (
            <p className="text-sm text-foreground">
              Working out what ties — this takes about half a minute on a full
              engagement.
            </p>
          )}
          {previewQ.isError && (
            <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
              <AlertTriangle size={18} className="mt-0.5 shrink-0" />
              Could not work out the offers. Nothing has been matched.
            </div>
          )}

          {done && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3">
              <p className="flex items-start gap-2 text-sm text-emerald-900">
                <Info size={18} className="mt-0.5 shrink-0" />
                {outcomeLine(done, tookEntries, tookParties)}
              </p>
              <p className="mt-1.5 text-sm text-muted-foreground">
                The company and exposure figures above will look unchanged, and
                that is correct: those count a company&apos;s two sides against each
                other, which matching entries inside a company does not alter.
                What has changed is how much is left to go through by hand.
              </p>
              {done.refused.length > 0 && (
                <p className="mt-1.5 text-xs text-emerald-900">
                  Refused: {done.refused.slice(0, 3).map((r) => r.reason).join(' · ')}
                  {done.refused.length > 3 ? ' …' : ''}
                </p>
              )}
            </div>
          )}

          {!done && plan && plan.batches.length === 0 && (
            <div className="rounded-md border border-border bg-muted p-3 text-sm text-foreground">
              <b>Nothing here for the machine to take.</b>
              <p className="mt-1">{why}</p>
            </div>
          )}

          {!done && plan?.batches.map((b) => (
            <div key={b.rule} className="rounded-lg border border-border p-3">
              <div className="flex items-start justify-between gap-4">
                <label className="flex cursor-pointer items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={rules.includes(b.rule)}
                    onChange={() => toggle(b.rule)}
                    disabled={run.isPending}
                  />
                  <span>
                    <span className="flex flex-wrap items-center gap-2">
                      <b className="text-sm text-foreground">{b.rule}</b>
                      <Chip tone="blue">{fmt.format(b.n)} groups</Chip>
                      <Chip tone="gray">{b.parties} {b.parties === 1 ? 'company' : 'companies'}</Chip>
                    </span>
                    {/* the rule shown IS the rule applied — built from the
                        figures in force, so the tolerance is the run's own */}
                    <span className="mt-0.5 block text-xs text-muted-foreground">{b.sentence}</span>
                  </span>
                </label>
                <Money v={b.tds} />
              </div>

              <p className="mb-1 mt-3 text-xs text-muted-foreground">
                {b.sample_covers_all
                  ? 'Every group in this batch:'
                  : `${b.sample.length} of ${fmt.format(b.n)}, chosen at this batch's edges — where a wrong match would show:`}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <tbody>
                    {b.sample.map((s, i) => (
                      <tr key={i}>
                        <td className="w-28 py-0.5"><Chip tone="gray" className="border border-border bg-transparent">{s.why}</Chip></td>
                        <td className="py-0.5">{s.party}</td>
                        <td className="w-24 py-0.5">{s.cardinality}</td>
                        <td className="w-28 py-0.5 text-right"><Money v={s.tds} className="text-xs" /></td>
                        <td className="w-32 py-0.5 text-right text-muted-foreground">differs by {s.residual}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {!done && plan && plan.batches.length > 0 && (
            <>
              {/* Never a silent cap: if the sweep stopped early the counts are
                  a floor, and "we did not look" must not read as "that's all". */}
              {plan.limited && (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                  The search stopped after {plan.parties_searched} companies, so
                  there may be more than this. Run it again afterwards to pick
                  up the rest.
                </div>
              )}
              {(plan.dup_review_needed > 0 || plan.ambiguous > 0) && (
                <p className="text-xs text-muted-foreground">
                  {plan.ambiguous > 0 && <>{plan.ambiguous} set(s) had more than
                    one possible answer and were left alone — a machine must not
                    choose between two sets that both tie. </>}
                  {plan.dup_review_needed > 0 && <>{fmt.format(plan.dup_review_needed)} entries
                    are identical repeats sharing one identity, so no match can
                    tell the copies apart — those stay open.</>}
                </p>
              )}
              {why === null && Object.keys(plan.skipped).length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Left for you: {Object.entries(plan.skipped)
                    .sort((a, b) => b[1] - a[1])
                    .map(([w, n]) => `${fmt.format(n)} ${w}`)
                    .join(' · ')}.
                </p>
              )}

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-base font-semibold text-foreground">
                      {fmt.format(summary!.entries)} entries
                      {summary!.isFloor ? ' or more' : ''} in{' '}
                      {fmt.format(summary!.offers)} groups
                    </p>
                    <p className="text-xs text-muted-foreground">
                      across {summary!.parties} companies · that is{' '}
                      {fmt.format(summary!.offers)} confirmations you do not have to click
                    </p>
                  </div>
                  <Money v={summary!.tds} />
                </div>
              </div>
            </>
          )}

          {run.isError && (
            <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
              <AlertTriangle size={18} className="mt-0.5 shrink-0" />
              Auto-match failed. Nothing was matched — the whole batch is one
              transaction, so there is no half-done state to clean up.
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Btn onClick={close}>{done ? 'Close' : 'Cancel'}</Btn>
            {!done && (
              <Btn kind="primary" onClick={confirm} loading={run.isPending}
                disabled={!summary || summary.offers === 0}>
                {run.isPending
                  ? 'Matching — this can take a minute'
                  : `Match ${fmt.format(summary?.entries ?? 0)} entries`}
              </Btn>
            )}
          </div>
        </div>
      </Modal>
    </>
  )
}
